import { GraviGrid } from '@gravitate-js/excalibrr'
import type { GraviGridProps } from '@gravitate-js/excalibrr/dist/components/GraviGrid/index.types'
import type { Filter } from '@gravitate-js/excalibrr/dist/components/Grid/SearchGridHeader/DynamicFilterForm/types'
import type { ICellRenderer, ICellRendererParams, IDetailCellRendererParams } from 'ag-grid-community'
import { useCallback, useEffect, useMemo } from 'react'

type FnOrObj<Args, Result> = ((args: Args) => Result) | Result

type Props<U, T extends { details?: Array<U> }, F extends Filter> = Omit<
  GraviGridProps<T, F>,
  'detailCellRendererParams'
> & {
  detailCellRendererParams?: FnOrObj<ICellRendererParams<T>, Partial<IDetailCellRendererParams<T, U>>>
}

/** Base table component that applies defaults to tables in the eBOL matching drawer */
export function BolBaseTable<U, T extends { details?: Array<U> }, F extends Filter>({
  agPropOverrides,
  detailCellRendererParams,
  ...props
}: Props<U, T, F>) {
  const defaultRendererParams = useCallback(
    (args: ICellRendererParams<T>): Partial<IDetailCellRendererParams<T, U>> => {
      const params =
        !!detailCellRendererParams && typeof detailCellRendererParams === 'function'
          ? detailCellRendererParams(args)
          : detailCellRendererParams
      return {
        fullWidth: true,
        getDetailRowData: ({ successCallback, data }) =>
          successCallback(data?.details?.map((x) => ({ ...x, parent: data })) ?? []),
        ...params,
        detailGridOptions: {
          pagination: false,
          domLayout: 'autoHeight',
          defaultColDef: {
            flex: 1,
          },
          ...params?.detailGridOptions,
        },
      }
    },
    [detailCellRendererParams]
  )

  return (
    <GraviGrid
      showColumnsToolbar={false}
      //@ts-ignore - we should fix this on excalibrr
      sideBar={false}
      masterDetail
      {...props}
      agPropOverrides={{
        suppressGroupRowsSticky: true,
        pagination: false,
        rowGroupPanelShow: 'never',
        getRowHeight: ({ node, api, data }) => {
          if (!node?.detail) return undefined
          const { rowHeight, headerHeight } = api.getSizesForCurrentTheme()
          return (rowHeight - 7) * (data?.details?.length ?? 0) + headerHeight
        },
        onFirstDataRendered: ({ api }) =>
          setTimeout(() => {
            api.getDisplayedRowAtIndex(0)?.setExpanded(true)
          }, 0),
        groupRowRendererParams: {
          totalValueGetter: '',
        },
        ...agPropOverrides,
      }}
      detailCellRendererParams={defaultRendererParams}
    />
  )
}
